/**
 * GlobalNotification: 상단 알림 카드 컴포넌트
 *
 * 마크업 기준: toast.html의 .rtcNewPopover.typeRequest
 * - 커스텀 텍스트 + highlight(<em>) 지원
 * - 커스텀 버튼 배열 (variant: default/blue/blue2)
 * - duration > 0이면 자동 소멸
 *
 * 전역 함수 showNotification()으로 호출.
 */
import React, { useCallback } from 'react';
import { useAppSelector } from '@/store/hooks';
import { executeButtonCallback, dismissNotification } from '@/utils/globalToast';
import type { GlobalNotification as NotificationType, NotificationButton } from '@/store/slices/toastSlice';
import { translateText } from '@/i18n';

// ─── 개별 알림 아이템 ─────────────────────────────────────────────────────

const NotificationItem: React.FC<{ notification: NotificationType }> = ({ notification }) => {
  const handleButtonClick = useCallback(
    (btn: NotificationButton) => {
      executeButtonCallback(btn.id);
    },
    [],
  );

  const handleDismiss = useCallback(() => {
    dismissNotification(notification.id);
  }, [notification.id]);

  return (
    <div className="rtcNewPopover typeRequest rtcCustoms_globalNotiItem">
      <div className="rtcNewPopover__request">
        <strong className="rtcNewPopover__requestText">
          {notification.highlight && <em>{notification.highlight}</em>}
          {translateText(notification.text)}
        </strong>
        <div className="btnBox">
          {notification.buttons.map((btn) => (
            <button
              key={btn.id}
              type="button"
              className={`rtcNewPopover__requestBtn${btn.variant && btn.variant !== 'default' ? ` ${btn.variant}` : ''}`}
              onClick={() => handleButtonClick(btn)}
            >
              {translateText(btn.label)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── 알림 컨테이너 ────────────────────────────────────────────────────────

const GlobalNotification: React.FC = () => {
  const notifications = useAppSelector((s) => s.toast.notifications);

  if (notifications.length === 0) return null;

  return (
    <div className="rtcCustoms_globalNotiContainer">
      {notifications.map((n) => (
        <NotificationItem key={n.id} notification={n} />
      ))}
    </div>
  );
};

export default GlobalNotification;
